/**
 * Every text input a member types into is `textUpdateMode: "onTyping"`, so the
 * composite's `onValueChanged` fires per keystroke rather than on blur.
 *
 * Questions that derive an answer from the address - chapter and region from
 * country plus postal code, rural status from a zip - watch these answers, and
 * on blur alone they only ever see a finished field. Per keystroke they keep
 * refining as it is typed, which is the behaviour `doNotChangeValue` on
 * `pillar_choice_dropdown` is written against.
 *
 * The hidden id fields are left alone: nothing types into them.
 */
export const profileAddressContectInformationQuestionElements = [
  {
    type: "text",
    name: "line1",
    title: "Street Address",
    textUpdateMode: "onTyping",
  },
  {
    type: "text",
    name: "line2",
    title: "Address Line 2",
    textUpdateMode: "onTyping",
  },
  {
    type: "text",
    name: "line3",
    title: "Address Line 3",
    textUpdateMode: "onTyping",
  },
  {
    type: "pillar_choice_dropdown",
    name: "country",
    title: "Country",
    pillar_choices_question_name: "country",
  },
  {
    type: "dropdown",
    name: "region",
    startWithNewLine: false,
    title: "Region",
    choices: [
      "Asia",
      "Africa",
      "Caribbean",
      "Europe",
      "North America",
      "Central America",
      "South America",
      "Austraila/ Oceania",
    ],
  },
  {
    type: "text",
    name: "city",
    title: "City",
    textUpdateMode: "onTyping",
  },
  {
    type: "text",
    name: "state",
    title: "State / Province / Region",
    startWithNewLine: false,
    textUpdateMode: "onTyping",
  },
  {
    type: "pillar_choice_dropdown",
    name: "us_state",
    startWithNewLine: false,
    title: "State",
    pillar_choices_question_name: "us_state",
  },
  {
    type: "text",
    name: "postal_code",
    startWithNewLine: false,
    title: "Zip / Postal Code",
    textUpdateMode: "onTyping",
  },
  {
    type: "text",
    name: "phone_display",
    title: "Phone",
    textUpdateMode: "onTyping",
    showInResults: false,
    placeholder: "XXXXXXXXXX",
    validators: [
      {
        type: "regex",
        text: "Please enter a valid phone number",
        regex:
          "\\+1 \\([0-9]{3}\\) [0-9]{3}-[0-9]{4}|\\([0-9]{3}\\) [0-9]{3}-[0-9]{4}|[0-9]{3}-[0-9]{3}-[0-9]{4}|^1 \\([0-9]{3}\\) [0-9]{3}-[0-9]{5}$|[0-9]{10}",
      },
    ],
  },
  {
    type: "expression",
    name: "phone",
    title: "Phone",
    visibleIf: "false",
  },
  {
    type: "text",
    name: "fax_display",
    required: false,
    startWithNewLine: false,
    title: "Fax",
    placeholder: "XXXXXXXXXX",
    showInResults: false,
    textUpdateMode: "onTyping",
    validators: [
      {
        type: "regex",
        text: "Please enter a valid fax number",
        regex:
          "\\+1 \\([0-9]{3}\\) [0-9]{3}-[0-9]{4}|\\([0-9]{3}\\) [0-9]{3}-[0-9]{4}|[0-9]{3}-[0-9]{3}-[0-9]{4}|^1 \\([0-9]{3}\\) [0-9]{3}-[0-9]{5}$|[0-9]{10}",
      },
    ],
  },
  {
    type: "expression",
    name: "fax",
    title: "Fax",
    visibleIf: "false",
  },
  {
    type: "text",
    name: "email",
    startWithNewLine: false,
    title: "Email",
    textUpdateMode: "onTyping",
  },
  {
    type: "text",
    name: "contact_information_id",
    title: "contact_information_id",
    readOnly: true,
    visible: false,
  },
  {
    type: "text",
    name: "profile_address_id",
    title: "profile_address_id",
    readOnly: true,
    visible: false,
  },
  {
    type: "checkbox",
    name: "use_id",
    title: "use_id",
    readOnly: true,
    visible: false,
  },
  {
    type: "checkbox",
    name: "use_ids",
    title: "use_ids",
    readOnly: true,
    visible: false,
  },
  {
    type: "text",
    name: "type_id",
    title: "type_id",
    readOnly: true,
    visible: false,
  },
] as const;

/**
 * Content elements that get no `_required` or `_visible` property: the hidden
 * id fields and the type/use values the question writes for itself.
 *
 * `onInit` skips these when it registers the per-element properties and
 * `setValuesFromProperties` skips them when it applies them, so the two have
 * to agree. They read this list rather than repeating it, and
 * `ProfileAddressContactInformationQuestionProperties` is derived from the
 * same list, so the registered properties and the declared ones cannot drift
 * apart.
 *
 * `type` is on the list but is not a content element - it is the legacy
 * spelling of `type_id`, kept here so a form design carrying it is skipped too.
 */
export const profileAddressContactInformationInternalElementNames = [
  "contact_information_id",
  "profile_address_id",
  "type",
  "type_id",
  "use_id",
  "use_ids",
] as const;

type ProfileAddressContactInformationElementName =
  (typeof profileAddressContectInformationQuestionElements)[number]["name"];

/**
 * The content elements a form designer can configure - every element except
 * the internal ones. `onInit` registers a `<name>_required` and a
 * `<name>_visible` property for each of these.
 */
export type ProfileAddressContactInformationConfigurableElementName = Exclude<
  ProfileAddressContactInformationElementName,
  (typeof profileAddressContactInformationInternalElementNames)[number]
>;
